import { createSelector } from '@reduxjs/toolkit'
import { z } from 'zod'
import { decodeJwtPayload } from 'auth/jwt'
import { areFlagOverridesAllowed } from 'features/devTools/flagOverrides'

const NAME_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
const NameClaimSchema = z.object({ [NAME_CLAIM]: z.string() })

/** The whole flags object. Only the Dev Tools panel wants this — everything else selects one flag. */
export const selectFlags = (state: { flags?: { flags?: Record<string, unknown> } }) => state?.flags?.flags ?? {}

export const makeUserIdFlagSelector = (flagName: string) =>
  createSelector([(state) => state?.flags?.flags, (state) => state?.auth], (flags, auth) => {
    const parsed = auth?.loggedIn ? NameClaimSchema.safeParse(decodeJwtPayload(auth.accessToken)) : null
    const userId = parsed?.success ? parsed.data[NAME_CLAIM] : null

    if (!userId) {
      return false
    }

    return flags[flagName]?.includes(userId) || flags[flagName]?.length === 0 || false
  })

export const selectDtsMoudPilotEnabled = makeUserIdFlagSelector('dts_moud_pilot')
export const selectPdmpPilotEnabled = makeUserIdFlagSelector('pdmp_pilot')

export const selectFlagsFetched = (state: Store) => state.flags.flagsFetched
export const selectAsyncRefillsPhase1Flag = (state: Store) => state.flags.flags.async_refills_phase_1
export const selectPharmacySearchDaw = (state: Store) => state.flags.flags.pharmacy_search_daw
export const selectDawShowTreatAndAdd = (state: Store) => state.flags.flags.daw_show_treat_and_add

/** Kill switch for the Kratom / 7-OH support-plan visit badge (QMD-4145). */
export const selectProgramVisitBadgeFlag = (state: Store) => state.flags.flags.program_visit_badge_provider

/**
 * PRJ-578: whether the live-visit pop-up shows the UI-only mock (made-up medication answers in
 * place of the intake form, and a made-up Home Delivery preference).
 *
 * Off unless someone pins `live_visit_hd_mock` from Dev Tools. The override check is repeated here
 * rather than trusted to the pin layer, so a stale persisted `true` cannot surface the mock where
 * production is served.
 */
export const selectLiveVisitHdMockEnabled = (state: Pick<Store, 'flags'>) =>
  areFlagOverridesAllowed() && state.flags.flags.live_visit_hd_mock === true

/** QMD-4694: kill switch for the Iterable-driven dashboard announcement placement. */
export const selectIterableAnnouncementsEnabled = (state: Store) =>
  state.flags.flags.iterable_announcements_provider === true
